import { FC, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { makeStyles, shorthands, Title1, tokens, Body1 } from '@fluentui/react-components';
import { LayoutGrid, MessageSquare, Settings, ChevronDown } from 'lucide-react';
import { IGptConfiguration } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { getGptIconAsset } from '../../Utils/image';
import { HorizontalSeparatorLine } from '../Common/HorizontalSeparatorLine';
import SyButton from '../Common/SyButton';
import { useIsMobile } from '../../Hooks/useResponsive';

const useStyles = makeStyles({
    page: {
        width: '100%',
        margin: '0 auto',
        overflowY: 'auto',
        backgroundColor: tokens.colorNeutralBackground3,
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        position: 'relative',
    },
    nameDropdown: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        background: 'none',
        border: 'none',
        borderRadius: tokens.borderRadiusLarge,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground2Hover,
        },
    },
    content : {
        display: 'flex',
        maxWidth: '900px',
        width: '100%',
        margin: '0 auto',
        flexDirection: 'column',
        ...shorthands.padding(tokens.spacingVerticalXXL),
        ...shorthands.gap(tokens.spacingVerticalXXL),
        '& h3': {
            color: 'var(--heading-text-color)',
        },

        '@media (max-width: 768px)': {
            width: '100%',
            maxWidth: '100%',
            ...shorthands.padding(tokens.spacingVerticalL),
            ...shorthands.gap(tokens.spacingVerticalL),
        },
    },
    profileCard: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusXLarge,
        alignItems: 'center',
        textAlign: 'center',
        ...shorthands.padding(tokens.spacingVerticalXXXL),
        paddingRight: '9px !important',
        '@media (max-width: 768px)': {
            ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingVerticalXXXL),
        },
    },
    avatar: {
        width: '64px',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tokens.colorBrandBackground2,
        borderRadius: tokens.borderRadiusXLarge,
        flexShrink: 0,
        '@media (max-width: 768px)': {
            width: '56px',
            height: '56px',
        },
    },
    avatarIcon: {
        fontSize: tokens.fontSizeBase400,
        color: tokens.colorBrandForeground1,
        '@media (max-width: 768px)': {
            fontSize: tokens.fontSizeBase300,
        },
    },
    profileTitle: {
        margin: `${tokens.spacingVerticalL} 0 0 0`,
        fontSize: '20px',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: '1.2',
        '@media (max-width: 768px)': {
            margin: `${tokens.spacingVerticalM} 0 0 0`,
            fontSize: tokens.fontSizeBase400,
        },
    },
    profileDescription: {
        maxWidth: '800px',
        lineHeight: '1.75',
        textAlign: 'center',
        margin: `${tokens.spacingVerticalM} 0 0 0`,
        fontSize: tokens.fontSizeBase400,
        color: 'var(--color-gray-500)',
        '@media (max-width: 768px)': {
            maxWidth: '100%',
            margin: `${tokens.spacingVerticalS} 0 0 0`,
            fontSize: tokens.fontSizeBase200,
            lineHeight: '1.5',
        },
    },
    actionButtons: {
        display: 'flex',
        marginTop: tokens.spacingVerticalXXL,
        ...shorthands.gap(tokens.spacingHorizontalM),
        flexWrap: 'wrap',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            width: '100%',
            marginTop: tokens.spacingVerticalL,
            ...shorthands.gap(tokens.spacingVerticalL),
        },
    },
    actionButton: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXL),
        '@media (max-width: 768px)': {
            width: '100%',
            justifyContent: 'center',
        },
    },
    previousDiscussions: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    discussionsList: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        ...shorthands.gap(tokens.spacingVerticalM),
        marginTop: tokens.spacingVerticalL,
        '@media (max-width: 768px)': {
            ...shorthands.gap(tokens.spacingVerticalS),
            marginTop: tokens.spacingVerticalM,
        },
    },
    discussionItem: {
        width: '100%',
        ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalXL),
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusLarge,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
            boxShadow: tokens.shadow8,
        },
        '@media (max-width: 768px)': {
            ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
        },
    },
    discussionContent: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalXS),
    },
    discussionTitle: {
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightMedium,
        lineHeight: tokens.lineHeightBase400,
        margin: 0,
        '&:hover': {
            color: tokens.colorBrandForeground1,
        },
        '@media (max-width: 768px)': {
            fontSize: tokens.fontSizeBase200,
        },
    },
    discussionTimestamp: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        margin: 0,
        lineHeight: '1.4',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.padding(`${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalM}`),
        textAlign: 'center',
        minHeight: '300px',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusXLarge,
        '& h3': {
            fontSize: tokens.fontSizeBase400,
            fontWeight: tokens.fontWeightSemibold,
            margin: `${tokens.spacingVerticalM} 0`,
        },
        '& p': {
            fontSize: tokens.fontSizeBase300,
            color: tokens.colorNeutralForeground2,
            margin: 0,
        },
    },
    emptyIcon: {
        fontSize: '2rem',
        color: tokens.colorNeutralForeground3,
        marginBottom: tokens.spacingVerticalL,
        opacity: 0.5,
    },
    sectionTitle: {
        margin: `0 0 ${tokens.spacingVerticalXS} 0`,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightMedium,
        lineHeight: '1.2',
        color: 'var(--color-neutral-400)',
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        ...shorthands.padding(`${tokens.spacingVerticalM} ${tokens.spacingVerticalM} ${tokens.spacingVerticalM} 17px`),
        marginLeft: '17px',
        width: '100%',
    },
    titleIcon: {
        fontSize: tokens.fontSizeBase400,
        color: tokens.colorNeutralForeground1,
        marginRight: '-17px'
    },
});

export interface ShowGptSummaryViewProps {
    gpt: IGptConfiguration | null;
    onConfigure?: (gpt: IGptConfiguration) => void;
    onStartDiscussion?: (gpt: IGptConfiguration) => void;
    onDiscussionClick?: (discussionId: string) => void;
}

export const ShowGptSummaryView: FC<ShowGptSummaryViewProps> = ({
    gpt,
    onConfigure,
    onStartDiscussion,
    onDiscussionClick,
}) => {
    const styles = useStyles();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    // Mock data for previous discussions - in a real scenario, this would come from props or API
    const previousDiscussions = [
        { id: '1', title: 'How to implement a password policy?', timestamp: '2 days ago' },
        { id: '2', title: 'Best practices for network security', timestamp: '5 days ago' },
        { id: '3', title: 'Data encryption standards and compliance', timestamp: '1 week ago' },
        { id: '4', title: 'Incident response procedures', timestamp: '2 weeks ago' },
    ];

    const [showDiscussions, setShowDiscussions] = useState(true);

    const handleConfigure = useCallback(() => {
        if (gpt && onConfigure) {
            onConfigure(gpt);
        }
    }, [gpt, onConfigure]);

    const handleStartDiscussion = useCallback(() => {
        if (gpt && onStartDiscussion) {
            onStartDiscussion(gpt);
        }
    }, [gpt, onStartDiscussion]);

    const handleDiscussionClick = useCallback((discussionId: string) => {
        if (onDiscussionClick) {
            onDiscussionClick(discussionId);
        }
    }, [onDiscussionClick]);

    if (!gpt) {
        return (
            <div className={styles.page}>
                <div className={styles.emptyState}>
                    <MessageSquare className={styles.emptyIcon} />
                    <h3>{t('exploreGpt.noGptSelected', 'No GPT selected')}</h3>
                    <p>
                        {t('exploreGpt.selectGpt', 'Please select a GPT to view its details.')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>

            {/* Header */}
            <div className="titleSection">
                <div className={styles.headerTitle}>
                    <LayoutGrid size={20} className={styles.titleIcon} />
                    <Title1 className="settingsTitle">
                        {gpt?.name}
                    </Title1>
                </div>
			</div>

            <div className={styles.content }>
                {/* GPT Profile Card */}
                <section className={styles.profileCard}>
                    <div className={styles.avatar}>
                        <LayoutGrid size={32} className={styles.avatarIcon} />
                    </div>
                    <h1 className={styles.profileTitle}>{gpt.name || 'Untitled GPT'}</h1>
                    <p className={styles.profileDescription}>
                        {gpt.description || t('exploreGpt.noDescription', 'No description available.')}
                    </p>

                    {/* Action Buttons */}
                    <div className={styles.actionButtons}>
                        {onConfigure && (
                            <SyButton
                                appearance="secondary"
                                className={styles.actionButton}
                                onClick={handleConfigure}
                                aria-label="Configure GPT"
                            >
                                <Settings size={16} />
                                <span>{t('exploreGpt.configure', 'Configure')}</span>
                            </SyButton>
                        )}
                        {onStartDiscussion && (
                            <SyButton
                                appearance="primary"
                                className={styles.actionButton}
                                onClick={handleStartDiscussion}
                                aria-label="Start a discussion"
                            >
                                <MessageSquare size={16} />
                                <span>{t('exploreGpt.startDiscussion', 'Start a discussion')}</span>
                            </SyButton>
                        )}
                    </div>
                </section>

                {/* Previous Discussions Section */}
                <section className={styles.previousDiscussions}>
                    <h2 className={styles.sectionTitle}>
                        {t('exploreGpt.previousDiscussions', 'Previous discussions').toUpperCase()}
                    </h2>
                    {showDiscussions && previousDiscussions.length > 0 ? (
                        <div className={styles.discussionsList}>
                            {previousDiscussions.map((discussion) => (
                                <article
                                    key={discussion.id}
                                    className={styles.discussionItem}
                                    tabIndex={0}
                                    onClick={() => handleDiscussionClick(discussion.id)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleDiscussionClick(discussion.id);
                                        }
                                    }}
                                >
                                    <div className={styles.discussionContent}>
                                        <h3 className={styles.discussionTitle}>
                                            {discussion.title}
                                        </h3>
                                        <p className={styles.discussionTimestamp}>
                                            {t('exploreGpt.lastMessage', 'Last message')} {discussion.timestamp}
                                        </p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            <MessageSquare className={styles.emptyIcon} />
                            <h3>{t('exploreGpt.noPreviousDiscussions', 'No previous discussions')}</h3>
                            <p>
                                {t('exploreGpt.startNewDiscussion', 'Start a new discussion to begin your conversation.')}
                            </p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default ShowGptSummaryView;
